/* assets/paint-finder.js */
(() => {
  "use strict";

  const DEFAULT_UI = {
    nextLabel: "Next",
    backLabel: "Back",
    finishLabel: "See my recommendation",
    progressLabel: "Step {current} of {total}",
    restartLabel: "Start over",
    ctaLabel: "View product",
    autoAdvanceSingle: true,  // auto next on single-choice questions
    autoFinishSingle: true    // if last visible question is single-choice, auto-finish on selection
  };

  function safeJsonParse(text) {
    try {
      return { ok: true, value: JSON.parse(text) };
    } catch (err) {
      return { ok: false, error: err };
    }
  }

  function formatMoneyFromCents(cents, currency) {
    const cur =
      currency ||
      (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) ||
      "USD";
    try {
      return new Intl.NumberFormat(undefined, { style: "currency", currency: cur }).format(
        (Number(cents) || 0) / 100
      );
    } catch {
      return `${((Number(cents) || 0) / 100).toFixed(2)} ${cur}`;
    }
  }

  function getShopRoot() {
    // Many Shopify themes expose Shopify.routes.root (or use "/" as fallback)
    const root =
      (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ||
      (window.routes && window.routes.root) ||
      "/";
    return String(root || "/");
  }

  function productJsUrl(handle) {
    const root = getShopRoot().replace(/\/+$/, "");
    return `${root}/products/${handle}.js`;
  }

  function normalizeToArray(v) {
    if (v == null) return [];
    return Array.isArray(v) ? v : [v];
  }

  function answerMatches(expected, actual) {
    const exp = normalizeToArray(expected);
    if (Array.isArray(actual)) return actual.some((x) => exp.includes(x));
    return exp.includes(actual);
  }

  function isQuestionVisible(q, answers) {
    if (!q || !q.showIf) return true;
    return Object.entries(q.showIf).every(([k, expected]) => {
      const actual = answers[k];
      if (actual == null) return false;
      return answerMatches(expected, actual);
    });
  }

  function matchesRule(when, answers) {
    if (!when) return false;
    return Object.entries(when).every(([qid, expected]) => {
      const actual = answers[qid];
      if (actual == null) return false;
      return answerMatches(expected, actual);
    });
  }

  async function hydrateProductFromShopify(productDef) {
    // productDef: { handle, title?, image?, features?, ... }
    if (!productDef || !productDef.handle) return { ok: false, value: productDef };

    try {
      const res = await fetch(productJsUrl(productDef.handle), { credentials: "same-origin" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const p = await res.json();

      const featuredImage =
        (p && p.featured_image) ||
        (p && p.images && p.images[0]) ||
        productDef.image ||
        "";

      const url = (p && p.url) || `${getShopRoot().replace(/\/+$/, "")}/products/${productDef.handle}`;

      const priceCents = (p && typeof p.price === "number" ? p.price : null);
      const compareAtCents = (p && typeof p.compare_at_price === "number" ? p.compare_at_price : null);

      return {
        ok: true,
        value: {
          ...productDef,
          title: productDef.title || p.title,
          image: productDef.image || featuredImage,
          url: productDef.url || url,
          priceCents: productDef.priceCents ?? priceCents,
          compareAtCents: productDef.compareAtCents ?? compareAtCents
        }
      };
    } catch {
      return { ok: true, value: productDef }; // fallback to config data
    }
  }

  class PaintFinder {
    constructor(appEl, config) {
      this.appEl = appEl;
      this.config = config || {};
      this.ui = { ...DEFAULT_UI, ...(this.config.ui || {}) };
      this.answers = {};
      this.index = 0;

      // Cache shell & start screen (same section instance)
      this.shell = appEl.closest(".pf-shell");
      this.startScreen = this.shell ? this.shell.querySelector(".pf-start") : null;

      // Results anchor passed from Liquid
      this.resultsAnchorId = appEl.getAttribute("data-results-anchor") || "";
    }

    get questionsAll() {
      return Array.isArray(this.config.questions) ? this.config.questions : [];
    }

    get questionsVisible() {
      return this.questionsAll.filter((q) => isQuestionVisible(q, this.answers));
    }

    get isLast() {
      return this.index >= this.questionsVisible.length - 1;
    }

    get currentQuestion() {
      return this.questionsVisible[this.index] || null;
    }

    mount() {
      this.appEl.innerHTML = "";
      this.appEl.hidden = false;

      this.appEl.insertAdjacentHTML(
        "beforeend",
        `
        <div class="pf-card">
          <div class="pf-progress">
            <div class="pf-progress__top">
              <div class="pf-progress__label" data-pf-progress-label></div>
            </div>
            <div class="pf-progress__track" aria-hidden="true">
              <div class="pf-progress__bar" data-pf-progress-bar></div>
            </div>
          </div>

          <div class="pf-body" data-pf-body></div>

          <div class="pf-nav">
            <button type="button" class="pf-btn pf-btn--ghost" data-pf-back>${this.ui.backLabel}</button>
            <button type="button" class="pf-btn pf-btn--primary" data-pf-next>${this.ui.nextLabel}</button>
          </div>
        </div>
      `
      );

      this.bind();
      this.render();
    }

    bind() {
      this.appEl.addEventListener("click", (e) => {
        const optBtn = e.target.closest("[data-pf-option]");
        if (optBtn) {
          const qid = optBtn.getAttribute("data-qid");
          const value = optBtn.getAttribute("data-value");
          if (!qid) return;

          this.setAnswer(qid, value);

          const q = this.currentQuestion;
          const isSingle = q && q.type === "single";
          if (isSingle && this.ui.autoFinishSingle && this.isLast) {
            this.finish();
            return;
          }
          if (isSingle && this.ui.autoAdvanceSingle && !this.isLast) {
            this.next();
          } else {
            this.render(); // update selected state
          }
          return;
        }

        const nextBtn = e.target.closest("[data-pf-next]");
        if (nextBtn) {
          if (this.isLast) this.finish();
          else this.next();
          return;
        }

        const backBtn = e.target.closest("[data-pf-back]");
        if (backBtn) {
          this.back();
          return;
        }

        const restartBtn = e.target.closest("[data-pf-restart]");
        if (restartBtn) {
          this.restart();
          return;
        }
      });
    }

    setAnswer(qid, value) {
      this.answers[qid] = value;

      // If visibility changes, clamp index safely
      const vis = this.questionsVisible;
      if (this.index > vis.length - 1) this.index = Math.max(0, vis.length - 1);
    }

    next() {
      const vis = this.questionsVisible;
      this.index = Math.min(this.index + 1, Math.max(0, vis.length - 1));
      this.render();
    }

    back() {
      this.index = Math.max(0, this.index - 1);
      this.render();
    }

    canGoNext() {
      const q = this.currentQuestion;
      if (!q) return false;
      return this.answers[q.id] != null && this.answers[q.id] !== "";
    }

    renderProgress() {
      const vis = this.questionsVisible;
      const total = Math.max(1, vis.length);
      const current = Math.min(total, this.index + 1);
      const label = (this.ui.progressLabel || DEFAULT_UI.progressLabel)
        .replace("{current}", String(current))
        .replace("{total}", String(total));

      const labelEl = this.appEl.querySelector("[data-pf-progress-label]");
      const barEl = this.appEl.querySelector("[data-pf-progress-bar]");
      if (labelEl) labelEl.textContent = label;
      if (barEl) barEl.style.width = `${Math.round((current / total) * 100)}%`;
    }

    renderQuestion() {
      const body = this.appEl.querySelector("[data-pf-body]");
      if (!body) return;

      const q = this.currentQuestion;
      if (!q) {
        body.innerHTML = `<p class="pf-muted">No questions configured.</p>`;
        return;
      }

      const selected = this.answers[q.id];

      const optionsHtml = (q.options || [])
        .map((opt) => {
          const isSelected = selected === opt.value;
          return `
            <button
              type="button"
              class="pf-option ${isSelected ? "is-selected" : ""}"
              data-pf-option
              data-qid="${q.id}"
              data-value="${opt.value}"
              aria-pressed="${isSelected ? "true" : "false"}"
            >
              <span class="pf-option__label">${opt.label}</span>
              ${opt.help ? `<span class="pf-option__help">${opt.help}</span>` : ""}
            </button>
          `;
        })
        .join("");

      body.innerHTML = `
        <div class="pf-q">
          <h3 class="pf-q__label">${q.label || ""}</h3>
          ${q.description ? `<div class="pf-q__desc">${q.description}</div>` : ""}
          <div class="pf-options">${optionsHtml}</div>
        </div>
      `;
    }

    renderNav() {
      const backBtn = this.appEl.querySelector("[data-pf-back]");
      const nextBtn = this.appEl.querySelector("[data-pf-next]");
      const vis = this.questionsVisible;

      if (backBtn) backBtn.disabled = this.index <= 0;
      if (nextBtn) {
        nextBtn.textContent = this.isLast ? this.ui.finishLabel : this.ui.nextLabel;
        nextBtn.disabled = !this.canGoNext();
      }

      // Edge case: if only 1 visible question and autoFinishSingle=false, still allow next after selection
      if (vis.length === 1 && nextBtn) nextBtn.disabled = !this.canGoNext();
    }

    render() {
      this.renderProgress();
      this.renderQuestion();
      this.renderNav();
    }

    pickRecommendation() {
      const rules = Array.isArray(this.config.rules) ? this.config.rules : [];
      const products = Array.isArray(this.config.products) ? this.config.products : [];

      const matched = rules
        .filter((r) => matchesRule(r.when, this.answers))
        .sort((a, b) => (b.priority || 0) - (a.priority || 0));

      const topRule = matched[0] || null;
      const fallbackId = this.config.fallbackProduct || (products[0] && products[0].id);

      const productId = (topRule && topRule.product) || fallbackId;
      const product = products.find((p) => p.id === productId) || null;

      return {
        rule: topRule,
        product
      };
    }

    async finish() {
      const body = this.appEl.querySelector("[data-pf-body]");
      if (!body) return;

      const { rule, product } = this.pickRecommendation();
      const resCfg = this.config.results || {};
      const title = resCfg.title || "Your recommendation";
      const subtitle = resCfg.subtitle || "Based on your answers.";

      if (!product) {
        body.innerHTML = `
          <div class="pf-results">
            <h3 class="pf-results__title">${title}</h3>
            <p class="pf-results__subtitle">${subtitle}</p>
            <p class="pf-muted">No matching product found. Please adjust your quiz rules.</p>
            <button type="button" class="pf-btn pf-btn--ghost" data-pf-restart>${this.ui.restartLabel}</button>
          </div>
        `;
        this.renderProgress();
        this.renderNavFinal();
        this.scrollToAnchor();
        return;
      }

      const hydrated = await hydrateProductFromShopify(product);
      const p = hydrated.value;

      const price =
        typeof p.priceCents === "number" ? formatMoneyFromCents(p.priceCents) : (p.price || "");
      const compareAt =
        typeof p.compareAtCents === "number" && p.compareAtCents > p.priceCents
          ? formatMoneyFromCents(p.compareAtCents)
          : "";

      const features = Array.isArray(p.features) ? p.features.slice(0, 3) : [];
      const featuresHtml = features.length
        ? `<ul class="pf-features">${features.map((f) => `<li>${f}</li>`).join("")}</ul>`
        : "";

      const reason = rule && rule.reason ? `<p class="pf-reason">${rule.reason}</p>` : "";

      body.innerHTML = `
        <div class="pf-results">
          <h3 class="pf-results__title">${title}</h3>
          <p class="pf-results__subtitle">${subtitle}</p>
          ${reason}

          <div class="pf-product">
            <div class="pf-product__media">
              ${p.image ? `<img class="pf-product__img" src="${p.image}" alt="${p.title || ""}" loading="lazy">` : ""}
            </div>
            <div class="pf-product__info">
              <h4 class="pf-product__title">${p.title || ""}</h4>
              ${
                price
                  ? `<div class="pf-product__price">
                      ${compareAt ? `<span class="pf-price--compare">${compareAt}</span>` : ""}
                      <span class="pf-price">${price}</span>
                    </div>`
                  : ""
              }
              ${featuresHtml}
              <div class="pf-product__actions">
                <a class="pf-btn pf-btn--primary" href="${p.url || "#"}">${this.ui.ctaLabel}</a>
                <button type="button" class="pf-btn pf-btn--ghost" data-pf-restart>${this.ui.restartLabel}</button>
              </div>
            </div>
          </div>
        </div>
      `;

      this.renderProgressFinal();
      this.renderNavFinal();
      this.scrollToAnchor();
    }

    renderProgressFinal() {
      const labelEl = this.appEl.querySelector("[data-pf-progress-label]");
      const barEl = this.appEl.querySelector("[data-pf-progress-bar]");
      if (labelEl) labelEl.textContent = "Done";
      if (barEl) barEl.style.width = "100%";
    }

    renderNavFinal() {
      const backBtn = this.appEl.querySelector("[data-pf-back]");
      const nextBtn = this.appEl.querySelector("[data-pf-next]");
      if (backBtn) backBtn.style.display = "none";
      if (nextBtn) nextBtn.style.display = "none";
    }

    restart() {
      this.answers = {};
      this.index = 0;

      // Restore nav buttons if they were hidden on finish
      const backBtn = this.appEl.querySelector("[data-pf-back]");
      const nextBtn = this.appEl.querySelector("[data-pf-next]");
      if (backBtn) backBtn.style.display = "";
      if (nextBtn) nextBtn.style.display = "";

      this.render();
    }

    scrollToAnchor() {
      if (!this.resultsAnchorId) return;
      const el = document.getElementById(this.resultsAnchorId);
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  }

  function injectBaseStylesOnce() {
    if (document.getElementById("pf-base-styles")) return;

    const style = document.createElement("style");
    style.id = "pf-base-styles";
    style.textContent = `
      .paint-finder-quiz .pf-card{
        border-radius: var(--pf-radius, 14px);
        padding: 24px;
        background: rgba(255,255,255,0.06);
        backdrop-filter: blur(6px);
        border: 1px solid rgba(255,255,255,0.10);
      }
      .paint-finder-quiz[data-pf-theme="light"] .pf-card{
        background: rgba(0,0,0,0.03);
        border-color: rgba(0,0,0,0.08);
      }
      .paint-finder-quiz .pf-progress__top{display:flex;justify-content:space-between;align-items:center;margin-bottom:10px;}
      .paint-finder-quiz .pf-progress__label{font-size:14px;opacity:.85}
      .paint-finder-quiz .pf-progress__track{height:8px;border-radius:999px;overflow:hidden;background: rgba(255,255,255,0.10);}
      .paint-finder-quiz[data-pf-theme="light"] .pf-progress__track{background: rgba(0,0,0,0.08);}
      .paint-finder-quiz .pf-progress__bar{height:100%;width:0;background: var(--pf-accent, #1b2f68);transition:width .25s ease;}

      .paint-finder-quiz .pf-q{margin-top:18px;}
      .paint-finder-quiz .pf-q__label{margin:0 0 10px;font-size:20px;line-height:1.25}
      .paint-finder-quiz .pf-q__desc{margin:0 0 14px;opacity:.9}
      .paint-finder-quiz .pf-options{display:grid;gap:10px;grid-template-columns:1fr;}
      @media (min-width: 750px){ .paint-finder-quiz .pf-options{grid-template-columns:1fr 1fr;} }

      .paint-finder-quiz .pf-option{
        text-align:left;
        border-radius: var(--pf-radius, 14px);
        padding: 14px 14px;
        border: 1px solid rgba(255,255,255,0.14);
        background: rgba(255,255,255,0.05);
        cursor:pointer;
        transition: transform .08s ease, border-color .12s ease, background .12s ease;
      }
      .paint-finder-quiz[data-pf-theme="light"] .pf-option{
        border-color: rgba(0,0,0,0.10);
        background: rgba(255,255,255,0.7);
      }
      .paint-finder-quiz .pf-option:hover{transform: translateY(-1px);}
      .paint-finder-quiz .pf-option.is-selected{
        border-color: var(--pf-accent, #1b2f68);
        box-shadow: 0 0 0 2px color-mix(in srgb, var(--pf-accent, #1b2f68) 30%, transparent);
      }
      .paint-finder-quiz .pf-option__label{display:block;font-weight:600;}
      .paint-finder-quiz .pf-option__help{display:block;font-size:13px;opacity:.85;margin-top:4px;}

      .paint-finder-quiz .pf-nav{display:flex;gap:12px;justify-content:space-between;margin-top:18px;}
      .paint-finder-quiz .pf-btn{
        display:inline-flex;align-items:center;justify-content:center;
        border-radius: 999px;
        padding: 12px 18px;
        border: 1px solid transparent;
        cursor:pointer;
        font-weight:700;
        text-decoration:none;
        line-height:1;
      }
      .paint-finder-quiz .pf-btn[disabled]{opacity:.5;cursor:not-allowed;}
      .paint-finder-quiz .pf-btn--primary{
        background: var(--pf-btn-bg, #F6A21A);
        color: var(--pf-btn-text, #111);
      }
      .paint-finder-quiz .pf-btn--ghost{
        background: transparent;
        border-color: rgba(255,255,255,0.20);
        color: inherit;
      }
      .paint-finder-quiz[data-pf-theme="light"] .pf-btn--ghost{border-color: rgba(0,0,0,0.15);}

      .paint-finder-quiz .pf-results__title{margin:0 0 8px;font-size:22px;}
      .paint-finder-quiz .pf-results__subtitle{margin:0 0 12px;opacity:.9}
      .paint-finder-quiz .pf-reason{margin:0 0 16px;opacity:.9}

      .paint-finder-quiz .pf-product{
        display:grid;gap:16px;grid-template-columns:1fr;
        border-radius: var(--pf-radius, 14px);
        border:1px solid rgba(255,255,255,0.12);
        background: rgba(255,255,255,0.04);
        padding: 14px;
      }
      .paint-finder-quiz[data-pf-theme="light"] .pf-product{
        border-color: rgba(0,0,0,0.10);
        background: rgba(255,255,255,0.7);
      }
      @media (min-width: 750px){ .paint-finder-quiz .pf-product{grid-template-columns: 220px 1fr;align-items:start;} }
      .paint-finder-quiz .pf-product__img{width:100%;height:auto;border-radius:12px;display:block;}
      .paint-finder-quiz .pf-product__title{margin:0 0 8px;font-size:18px;}
      .paint-finder-quiz .pf-product__price{display:flex;gap:10px;align-items:baseline;margin:0 0 10px;}
      .paint-finder-quiz .pf-price--compare{text-decoration:line-through;opacity:.7}
      .paint-finder-quiz .pf-features{margin:0 0 14px;padding-left:18px;}
      .paint-finder-quiz .pf-product__actions{display:flex;gap:10px;flex-wrap:wrap;}
      .paint-finder-quiz .pf-muted{opacity:.85}
    `;
    document.head.appendChild(style);
  }

  function initAll() {
    injectBaseStylesOnce();

    // Start buttons (your Liquid already has data-pf-start + data-pf-target)
    document.querySelectorAll("[data-pf-start]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const targetSel = btn.getAttribute("data-pf-target");
        const app = targetSel ? document.querySelector(targetSel) : null;
        if (!app) return;

        const shell = app.closest(".pf-shell");
        const start = shell ? shell.querySelector(".pf-start") : null;
        if (start) start.hidden = true;

        // Parse config
        const configId = app.getAttribute("data-config-id");
        const script = configId ? document.getElementById(configId) : null;
        const raw = script ? script.textContent.trim() : "";

        const parsed = safeJsonParse(raw);
        if (!parsed.ok) {
          app.hidden = false;
          app.innerHTML = `
            <div class="pf-card">
              <p class="pf-muted"><strong>Paint Finder config error:</strong> Invalid JSON.</p>
              <pre style="white-space:pre-wrap;opacity:.85">${String(parsed.error)}</pre>
            </div>
          `;
          return;
        }

        const quiz = new PaintFinder(app, parsed.value);
        quiz.mount();
      });
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
  } else {
    initAll();
  }
})();
